/**
 * Notifications hook - stub implementation
 *
 * @requirement BRD-000 Foundational Screens
 * @screen SCR-000 Application Shell - Notifications
 * @screen SCR-004 Notifications Center
 */

import { useQuery } from '@tanstack/react-query';
import type { Notification, NotificationType } from '@myvault/shared/types';

export interface NotificationDisplay {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  timeAgo: string;
  isRead: boolean;
  actionUrl?: string;
}

function formatTimeAgo(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Zojuist';
  if (diffMins < 60) return `${diffMins} min geleden`;
  if (diffHours < 24) return `${diffHours} uur geleden`;
  if (diffDays === 1) return 'Gisteren';
  if (diffDays < 7) return `${diffDays} dagen geleden`;
  return date.toLocaleDateString('nl-NL');
}

function transformNotification(notification: Notification): NotificationDisplay {
  return {
    id: notification.id,
    type: notification.type,
    title: notification.title,
    message: notification.message,
    timeAgo: formatTimeAgo(notification.createdAt),
    isRead: notification.read,
    actionUrl: notification.actionUrl,
  };
}

// Stub data for development
const stubNotifications: Notification[] = [];

export function useNotifications(_limit = 10) {
  const notificationsQuery = useQuery({
    queryKey: ['notifications'],
    queryFn: async (): Promise<NotificationDisplay[]> => {
      // TODO: Connect to real API
      return stubNotifications.map(transformNotification);
    },
    staleTime: 30000,
  });

  const unreadCountQuery = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: async (): Promise<number> => {
      // TODO: Connect to real API
      return 0;
    },
    staleTime: 30000,
  });

  return {
    notifications: notificationsQuery.data ?? [],
    isLoading: notificationsQuery.isLoading,
    unreadCount: unreadCountQuery.data ?? 0,
    markAsRead: (_id: string) => {
      // TODO: Implement
    },
    markAllAsRead: () => {
      // TODO: Implement
    },
    isMarkingRead: false,
    isMarkingAllRead: false,
  };
}
